import { createContext, useContext, type PropsWithChildren, type ReactElement } from 'react';

/**
 * Minimal item shape required by shared dictionary cells.
 * Real dictionary items can have more fields — only these are used for display.
 */
export interface IDictionaryItem {
  name: string,
  abbreviation?: string,
  displayName?: string,
  code?: string,
}

export type TDictionaryId = string | number;

export interface IDictionaryAccessor<T extends IDictionaryItem = IDictionaryItem> {
  getById: (id: TDictionaryId | null) => T | undefined,
  getAll: () => T[],
  has: (id: TDictionaryId | null) => boolean,
}

export type ITableDictionaries = Record<string, IDictionaryAccessor>;

const TableDictionariesContext = createContext<ITableDictionaries | null>(null);

export const useTableDictionaries = (): ITableDictionaries => {
  const context = useContext(TableDictionariesContext);

  if (!context) {
    throw new Error('useTableDictionaries must be used within TableDictionariesProvider');
  }

  return context;
};

interface IProps extends PropsWithChildren {
  dictionaries: ITableDictionaries,
}

export const TableDictionariesProvider = ({ children, dictionaries }: IProps): ReactElement => (
  <TableDictionariesContext.Provider value={ dictionaries }>
    { children }
  </TableDictionariesContext.Provider>
);
